/**
 * The account button and its menu: who you are signed in as, theme, density, sign out.
 *
 * Theme and density live here because they are per-person settings, and that is what
 * this menu is for. On desktop the masthead carries the theme switch (ThemeControl),
 * so the menu only repeats it at phone width, where the masthead has no room for it.
 */
import { useEffect, useRef, useState } from 'react'
import { useAuth, isDevBypass } from '@/auth/AuthProvider'
import { useMediaQuery } from '@/ui/useMediaQuery'
import {
  usePreferences,
  THEMES,
  DENSITIES,
  type Theme,
  type Density,
} from '@/ui/preferences'
import { useDevMode } from '@/ui/devMode'

/** Initials from a display name, falling back to the email's first letters. */
function initials(name: string | undefined, email: string): string {
  const source = name?.trim() || email.split('@')[0]?.replace(/[._-]+/g, ' ') || '?'
  const parts = source.split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}

function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: T
  options: { value: T; label: string }[]
  onChange: (v: T) => void
}) {
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <span className="eyebrow">{label}</span>
      <div
        role="radiogroup"
        aria-label={label}
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${options.length}, 1fr)`,
          border: '1px solid var(--rule-2)',
          borderRadius: 7,
          overflow: 'hidden',
        }}
      >
        {options.map((o) => {
          const on = o.value === value
          return (
            <button
              key={o.value}
              role="radio"
              aria-checked={on}
              onClick={() => onChange(o.value)}
              style={{
                border: 0,
                borderLeft: '1px solid var(--rule-2)',
                padding: '6px 4px',
                fontSize: 'var(--t-table)',
                cursor: 'pointer',
                background: on ? 'var(--accent-soft)' : 'var(--surface)',
                color: on ? 'var(--accent-ink)' : 'var(--ink-2)',
                fontWeight: on ? 500 : 400,
              }}
            >
              {o.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Theme (phone width only) and density. In this menu, or — when the FCC Portal bar is on
 * — slotted into the bar's account menu instead (PortalBar), so there is one disc, not two.
 */
/**
 * The dev mode switch (src/ui/devMode.tsx), for comment reviewers only: Dev mode governs
 * Comments and nothing else (View as and Rules show whenever held). Lives with the
 * preferences so it is in the portal bar's menu too.
 */
export function DevModeSwitch() {
  const { available, on, setOn } = useDevMode()
  if (!available) return null
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <Segmented<'off' | 'on'>
        label="Dev mode"
        value={on ? 'on' : 'off'}
        options={[
          { value: 'off', label: 'Off' },
          { value: 'on', label: 'On' },
        ]}
        onChange={(v) => setOn(v === 'on')}
      />
      <span style={{ fontSize: 'var(--t-rank)', color: 'var(--ink-3)' }}>
        {on ? 'Showing review comments.' : 'Off: review comments hidden. On shows them.'}
      </span>
    </div>
  )
}

export function PreferenceControls() {
  const { theme, density, setTheme, setDensity } = usePreferences()
  // Same breakpoint as the masthead's ThemeControl: one place per width, never two.
  const wide = useMediaQuery('(min-width: 900px)')
  return (
    <>
      {!wide && (
        <Segmented<Theme>
          label="Theme"
          value={theme}
          options={THEMES}
          onChange={setTheme}
        />
      )}

      <div style={{ display: 'grid', gap: 6 }}>
        <Segmented<Density>
          label="Density"
          value={density}
          options={DENSITIES.map((d) => ({ value: d.value, label: d.label }))}
          onChange={setDensity}
        />
        <span style={{ fontSize: 'var(--t-rank)', color: 'var(--ink-3)' }}>
          {DENSITIES.find((d) => d.value === density)?.note}
        </span>
      </div>

      <DevModeSwitch />
    </>
  )
}

export function UserMenu() {
  const { account, signOut } = useAuth()
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('mousedown', onClick)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('mousedown', onClick)
    }
  }, [open])

  const email = account?.username ?? ''
  const name = account?.name
  // With no account (local dev bypass) the disc shows a person glyph. It used to show
  // an em dash, which read as a broken toggle rather than as "nobody signed in".
  const mark = account ? (
    initials(name, email)
  ) : (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx="8" cy="5.5" r="3" />
      <path d="M2.5 14.5c.6-3 2.8-4.5 5.5-4.5s4.9 1.5 5.5 4.5" />
    </svg>
  )

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={account ? `Account: ${name ?? email}` : 'Settings'}
        title={account ? (name ?? email) : 'Settings'}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          background: open ? 'var(--surface-3)' : 'none',
          border: '1px solid var(--rule)',
          borderRadius: 20,
          padding: '3px 9px 3px 3px',
          cursor: 'pointer',
          color: 'var(--ink-2)',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            background: 'var(--accent-soft)',
            color: 'var(--accent-ink)',
            display: 'grid',
            placeItems: 'center',
            fontFamily: 'var(--mono)',
            fontSize: 'var(--t-rank)',
            fontWeight: 600,
            letterSpacing: '0.02em',
          }}
        >
          {mark}
        </span>
        <span
          className="mono"
          style={{ fontSize: 'var(--t-micro)', color: 'var(--ink-3)' }}
          aria-hidden="true"
        >
          ▾
        </span>
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 8px)',
            width: 268,
            background: 'var(--surface)',
            border: '1px solid var(--rule)',
            borderRadius: 10,
            boxShadow: 'var(--shadow)',
            padding: 14,
            display: 'grid',
            gap: 14,
            zIndex: 60,
          }}
        >
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span
              aria-hidden="true"
              style={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                background: 'var(--accent-soft)',
                color: 'var(--accent-ink)',
                display: 'grid',
                placeItems: 'center',
                fontFamily: 'var(--mono)',
                fontSize: 13,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {mark}
            </span>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13.5,
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {name ?? (account ? email : 'Not signed in')}
              </div>
              <div
                className="mono"
                style={{
                  fontSize: 'var(--t-micro)',
                  color: 'var(--ink-3)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={email}
              >
                {account ? email : isDevBypass ? 'local development' : 'no account'}
              </div>
            </div>
          </div>

          <div style={{ height: 1, background: 'var(--rule)' }} />

          <PreferenceControls />

          {account && (
            <>
              <div style={{ height: 1, background: 'var(--rule)' }} />
              <button
                onClick={() => void signOut()}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  background: 'none',
                  border: '1px solid var(--rule-2)',
                  borderRadius: 7,
                  padding: '7px 10px',
                  fontSize: 13,
                  cursor: 'pointer',
                  color: 'var(--ink-2)',
                }}
              >
                Sign out
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
